// A GameRecord is a self-contained snapshot of everything needed to replay
// a match from scratch: the RNG seed, the rule bundle, both decks, and the
// ordered action log. Combined with the live card catalog, the replay tool
// (`tools/replay-log.ts`) reconstructs the final state and asserts the
// recorded hash matches the recomputed hash.
//
// This is the spine of Phase 1's regression discipline — every later change
// to engine code re-runs recorded games and must reproduce them byte-for-
// byte. It's also the wire format we'll use later for server-side log
// validation (Phase 4) without needing a fresh schema.

import type { GameState, GameAction, DeckDef } from '../types.js';
import type { GameRule } from '../shared/rules.js';

export interface GameRecord {
  /** Schema version of the record envelope itself. Bumped if we ever
   *  change what's captured here. */
  version: 1;
  /** Initial PRNG seed. Replays createInitialState() with this seed to
   *  reproduce deck shuffles and the coin flip. */
  seed: number;
  /** Rule bundle that was active. May be undefined for skirmishes that
   *  use defaults. */
  rule?: GameRule;
  playerDeck: DeckDef;
  opponentDeck: DeckDef;
  /** Every action dispatched, in order. SKIP_PHASE / CONFIRM_BLOCKERS etc.
   *  are included — the replay tool drives the engine through the same
   *  TurnManager path as the live game. */
  actions: GameAction[];
  /** Canonical-JSON hash (FNV-1a, hex) of the final state — see hashState
   *  in this file. The replay tool recomputes and compares. */
  finalStateHash: string;
  /** UTC timestamp, recorded at game end. Diagnostic only. */
  recordedAt: number;
}

/** Build a canonical JSON representation of state that omits engine-
 *  internal cosmetic fields and orders Maps deterministically. Used as the
 *  basis for `hashState` and for human-readable diffing when a replay
 *  diverges. */
export function serializeStateForHash(state: GameState): string {
  const instances: Record<string, unknown> = {};
  const ids = [...state.cardInstances.keys()].sort();
  for (const id of ids) {
    const inst = state.cardInstances.get(id)!;
    instances[id] = {
      cardId: inst.cardId,
      owner: inst.owner,
      currentPower: inst.currentPower,
      armor: inst.armor,
      isFresh: inst.isFresh,
      isExhausted: inst.isExhausted,
      supports: [...inst.supports],
      zone: inst.zone,
      skipNextRefresh: inst.skipNextRefresh,
      abilityDisabled: inst.abilityDisabled,
    };
  }
  const combat = state.combatState
    ? {
        groups: state.combatState.groups.map(g => ({ attackerIds: [...g.attackerIds], united: g.united })),
        blockerAssignments: [...state.combatState.blockerAssignments.entries()]
          .sort((a, b) => a[0] - b[0]),
      }
    : null;
  const playerSlice = (ps: GameState['player']) => ({
    hp: ps.hp,
    spirit: ps.spirit,
    deck: ps.deck,
    hand: ps.hand,
    ring: ps.ring.map(s => s.instanceId),
    discardPile: ps.discardPile,
    attackLine: ps.attackLine,
    ralliesUsed: ps.ralliesUsed,
    supportsUsed: ps.supportsUsed,
    bonusRallies: ps.bonusRallies,
    bonusSupports: ps.bonusSupports,
    lastActivateHp: ps.lastActivateHp,
    lastActivateDeck: ps.lastActivateDeck,
    lastTurnHpDelta: ps.lastTurnHpDelta,
    lastTurnDeckDelta: ps.lastTurnDeckDelta,
    attackedDuringTurn: ps.attackedDuringTurn,
  });
  const canonical = {
    turnNumber: state.turnNumber,
    currentPlayer: state.currentPlayer,
    firstPlayer: state.firstPlayer,
    phase: state.phase,
    isFirstTurn: state.isFirstTurn,
    clock: state.clock,
    maxHp: state.maxHp,
    winner: state.winner,
    winReason: state.winReason,
    player: playerSlice(state.player),
    opponent: playerSlice(state.opponent),
    cardInstances: instances,
    combatState: combat,
    // Note: `log` is intentionally omitted from the hash. Log strings are
    // narrative output that may include cosmetic markup tweaks; we hash on
    // the gameplay-relevant state only. Same reasoning for the voice-line
    // latches and the rng state itself (the rng is an *input* to the hash,
    // not an output — the replay seeds from `seed`, so its end-state stream
    // position is implicit in the action sequence).
    nextInstanceId: state.nextInstanceId,
  };
  return JSON.stringify(canonical);
}

/** FNV-1a 32-bit hash of a string. Tiny, dependency-free, plenty for the
 *  replay-divergence check we need (we're not defending against adversarial
 *  collisions; just catching accidental engine drift). */
export function fnv1a(input: string): string {
  let hash = 0x811C9DC5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function hashState(state: GameState): string {
  return fnv1a(serializeStateForHash(state));
}
